import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { statusColor, priorityColor, inr, fmtDate } from "@/lib/utils-format";
import { Plus, Sparkles, Loader2, UserCheck, Trash2 } from "lucide-react";
import { toast } from "sonner";

const STATUSES = ["new", "contacted", "qualified", "proposal", "won", "lost"];
const PRIORITIES = ["low", "medium", "high", "urgent"];
const SOURCES = ["website", "referral", "walk-in", "instagram", "cold-call", "other"];

const emptyLead = {
  name: "", company: "", email: "", phone: "", source: "website",
  status: "new", priority: "medium", expected_value: 0,
  products_interested: "", notes: "", assigned_to: "",
};

export default function LeadsPage() {
  const [leads, setLeads] = useState([]);
  const [users, setUsers] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(emptyLead);
  const [editingId, setEditingId] = useState(null);
  const [view, setView] = useState("kanban"); // kanban | list
  const [scoring, setScoring] = useState(null);

  const load = async () => {
    const [l, u] = await Promise.all([api.get("/leads"), api.get("/users")]);
    setLeads(l.data);
    setUsers(u.data);
  };

  useEffect(() => { load(); }, []);

  const submit = async () => {
    try {
      const payload = { ...form, expected_value: Number(form.expected_value) || 0, assigned_to: form.assigned_to || null };
      if (editingId) await api.patch(`/leads/${editingId}`, payload);
      else await api.post("/leads", payload);
      toast.success(editingId ? "Lead updated" : "Lead created");
      setOpen(false);
      setForm(emptyLead);
      setEditingId(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  };

  const openEdit = (l) => {
    setForm({ ...emptyLead, ...l, expected_value: l.expected_value || 0, assigned_to: l.assigned_to || "" });
    setEditingId(l.id);
    setOpen(true);
  };

  const changeStatus = async (lead, status) => {
    await api.patch(`/leads/${lead.id}`, { status });
    load();
  };

  const score = async (lead) => {
    setScoring(lead.id);
    try {
      const { data } = await api.post(`/leads/${lead.id}/score`);
      toast.success(`AI Score: ${data.score}/100`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "AI scoring failed");
    } finally {
      setScoring(null);
    }
  };

  const convert = async (lead) => {
    if (!window.confirm(`Convert ${lead.name} to a customer?`)) return;
    try {
      await api.post(`/leads/${lead.id}/convert`);
      toast.success("Lead converted to customer");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  };

  const remove = async (lead) => {
    if (!window.confirm(`Delete lead ${lead.name}?`)) return;
    try {
      await api.delete(`/leads/${lead.id}`);
      toast.success("Deleted");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  };

  return (
    <div className="space-y-6" data-testid="leads-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="uc-label">Pipeline</div>
          <h1 className="font-[Outfit] text-3xl sm:text-4xl font-semibold tracking-tight mt-1">
            Lead Management
          </h1>
        </div>
        <div className="flex items-center gap-2">
          <div className="inline-flex rounded-md border border-[#EAE5D9] bg-white p-0.5">
            <button
              onClick={() => setView("kanban")}
              data-testid="leads-view-kanban"
              className={`px-3 py-1.5 text-xs rounded-[4px] transition-colors ${view === "kanban" ? "bg-[#C05621] text-white" : "text-[#5C4033]"}`}
            >
              Kanban
            </button>
            <button
              onClick={() => setView("list")}
              data-testid="leads-view-list"
              className={`px-3 py-1.5 text-xs rounded-[4px] transition-colors ${view === "list" ? "bg-[#C05621] text-white" : "text-[#5C4033]"}`}
            >
              List
            </button>
          </div>
          <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) { setForm(emptyLead); setEditingId(null); } }}>
            <DialogTrigger asChild>
              <Button data-testid="lead-add-btn" className="bg-[#C05621] hover:bg-[#9C4221] text-white">
                <Plus className="w-4 h-4 mr-1.5" /> New Lead
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl bg-white" data-testid="lead-form-dialog">
              <DialogHeader>
                <DialogTitle className="font-[Outfit]">{editingId ? "Edit Lead" : "New Lead"}</DialogTitle>
              </DialogHeader>
              <div className="grid grid-cols-2 gap-4 py-2">
                <div><Label>Name *</Label><Input data-testid="lead-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
                <div><Label>Company</Label><Input value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} /></div>
                <div><Label>Email</Label><Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></div>
                <div><Label>Phone</Label><Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} /></div>
                <div>
                  <Label>Source</Label>
                  <Select value={form.source} onValueChange={(v) => setForm({ ...form, source: v })}>
                    <SelectTrigger data-testid="lead-source-select"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-white">
                      {SOURCES.map((s) => <SelectItem key={s} value={s} className="capitalize">{s}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Status</Label>
                  <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                    <SelectTrigger data-testid="lead-status-select"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-white">
                      {STATUSES.map((s) => <SelectItem key={s} value={s} className="capitalize">{s}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Priority</Label>
                  <Select value={form.priority} onValueChange={(v) => setForm({ ...form, priority: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-white">
                      {PRIORITIES.map((s) => <SelectItem key={s} value={s} className="capitalize">{s}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div><Label>Expected Value (₹)</Label><Input type="number" value={form.expected_value} onChange={(e) => setForm({ ...form, expected_value: e.target.value })} /></div>
                <div className="col-span-2"><Label>Products Interested</Label><Input placeholder="e.g. Cakes, cookies, breads" value={form.products_interested} onChange={(e) => setForm({ ...form, products_interested: e.target.value })} /></div>
                <div>
                  <Label>Assigned to</Label>
                  <Select value={form.assigned_to || "unassigned"} onValueChange={(v) => setForm({ ...form, assigned_to: v === "unassigned" ? "" : v })}>
                    <SelectTrigger><SelectValue placeholder="Unassigned" /></SelectTrigger>
                    <SelectContent className="bg-white">
                      <SelectItem value="unassigned">Unassigned</SelectItem>
                      {users.map((u) => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="col-span-2"><Label>Notes</Label><Textarea rows={3} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
                <Button onClick={submit} data-testid="lead-save-btn" className="bg-[#C05621] hover:bg-[#9C4221] text-white">Save</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {view === "kanban" ? (
        <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-3">
          {STATUSES.map((st) => {
            const items = leads.filter((l) => l.status === st);
            const total = items.reduce((s, l) => s + Number(l.expected_value || 0), 0);
            return (
              <div key={st} className="kanban-col rounded-md p-3" data-testid={`kanban-col-${st}`}>
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <Badge className={`capitalize ${statusColor(st)}`} variant="outline">{st}</Badge>
                    <span className="text-xs text-[#8B7355]">{items.length}</span>
                  </div>
                  <div className="text-[10px] text-[#8B7355]">{inr(total)}</div>
                </div>
                <div className="space-y-2 min-h-[40px]">
                  {items.map((l) => (
                    <div
                      key={l.id}
                      data-testid={`lead-card-${l.id}`}
                      className="bg-white border border-[#EAE5D9] rounded-md p-3 hover-lift cursor-pointer"
                      onClick={() => openEdit(l)}
                    >
                      <div className="flex items-start justify-between gap-2">
                        <div className="min-w-0">
                          <div className="font-semibold text-sm truncate">{l.name}</div>
                          {l.company && <div className="text-xs text-[#8B7355] truncate">{l.company}</div>}
                        </div>
                        {l.ai_score != null && (
                          <div className="text-[10px] bg-[#FEEBC8] text-[#7B341E] px-1.5 py-0.5 rounded font-semibold">
                            AI {l.ai_score}
                          </div>
                        )}
                      </div>
                      <div className="mt-2 flex items-center gap-1.5 flex-wrap">
                        <Badge variant="outline" className={`capitalize text-[10px] ${priorityColor(l.priority)}`}>{l.priority}</Badge>
                        {l.expected_value > 0 && <span className="text-[10px] text-[#5C4033]">{inr(l.expected_value)}</span>}
                      </div>
                      <div className="mt-2 flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
                        <button
                          onClick={() => score(l)}
                          data-testid={`lead-score-btn-${l.id}`}
                          disabled={scoring === l.id}
                          className="text-[10px] px-2 py-1 rounded bg-[#2C1810] text-white hover:bg-[#3a2418] disabled:opacity-50 flex items-center gap-1"
                        >
                          {scoring === l.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <Sparkles className="w-3 h-3" />} Score
                        </button>
                        {l.status !== "won" && (
                          <button
                            onClick={() => convert(l)}
                            data-testid={`lead-convert-btn-${l.id}`}
                            className="text-[10px] px-2 py-1 rounded bg-emerald-600 text-white hover:bg-emerald-700 flex items-center gap-1"
                          >
                            <UserCheck className="w-3 h-3" /> Convert
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                  {items.length === 0 && (
                    <div className="text-xs text-[#8B7355] italic text-center py-4">Empty</div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <div className="bg-white border border-[#EAE5D9] rounded-lg overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left uc-label border-b border-[#EAE5D9]">
                <th className="px-4 py-3">Name</th>
                <th className="px-4 py-3">Company</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3">Priority</th>
                <th className="px-4 py-3 text-right">Expected</th>
                <th className="px-4 py-3">AI Score</th>
                <th className="px-4 py-3">Created</th>
                <th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {leads.map((l) => (
                <tr key={l.id} className="border-b border-[#EAE5D9] last:border-0 hover:bg-[#FDFBF7]" data-testid={`lead-row-${l.id}`}>
                  <td className="px-4 py-3 font-medium cursor-pointer" onClick={() => openEdit(l)}>{l.name}</td>
                  <td className="px-4 py-3 text-[#5C4033]">{l.company || "—"}</td>
                  <td className="px-4 py-3">
                    <Select value={l.status} onValueChange={(v) => changeStatus(l, v)}>
                      <SelectTrigger className="h-8 w-32"><SelectValue /></SelectTrigger>
                      <SelectContent className="bg-white">
                        {STATUSES.map((s) => <SelectItem key={s} value={s} className="capitalize">{s}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </td>
                  <td className="px-4 py-3"><Badge variant="outline" className={`capitalize ${priorityColor(l.priority)}`}>{l.priority}</Badge></td>
                  <td className="px-4 py-3 text-right">{inr(l.expected_value || 0)}</td>
                  <td className="px-4 py-3">
                    {l.ai_score != null ? (
                      <span className="text-[10px] bg-[#FEEBC8] text-[#7B341E] px-1.5 py-0.5 rounded font-semibold">{l.ai_score}</span>
                    ) : "—"}
                  </td>
                  <td className="px-4 py-3 text-[#8B7355]">{fmtDate(l.created_at)}</td>
                  <td className="px-4 py-3 text-right">
                    <button onClick={() => remove(l)} data-testid={`lead-delete-btn-${l.id}`} className="text-red-600 hover:text-red-700">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </td>
                </tr>
              ))}
              {leads.length === 0 && (
                <tr><td colSpan={8} className="text-center py-10 text-[#8B7355]">No leads yet. Click <b>New Lead</b> to add.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
